import React, { Suspense, useState, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import widgetRegistry from '../widgets/widgetRegistry';
import { EditSaveButton, WidgetSelectionModal, ConfirmDeleteModal } from '../components';
import WidgetLoader from './WidgetLoader';
import { useDynamicPageWidgets } from '../hooks/useDynamicPageWidgets';
import { useNavSpacing } from '../hooks/useNavSpacing';
import { updateSiteContentRequest } from '../redux/slices/siteContentSlice';
import { toggleEditMode } from '../redux/slices/editModeSlice';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { MdWidgets, MdAdd, MdOutlineInbox } from 'react-icons/md';
import ErrorPage from '../routes/ErrorPage';

interface DynamicPageProps {
  pageType: string;
  showEditButton?: boolean;
  children?: React.ReactNode;
}

const DynamicPage: React.FC<DynamicPageProps> = ({
  pageType,
  showEditButton = true,
  children
}) => {
  const navSpacing = useNavSpacing();
  const {isEditControl } = useAppSelector((state) => state?.editControl);
  const updateLoading = useAppSelector((state) => state?.siteContent?.updateLoading);
  const dispatch = useAppDispatch();

  // Use the dynamic page widgets hook
  const {
    widgets,
    updateWidgetData,
    toggleEditModeWithSave,
    addWidget,
    addMultipleWidgets,
    deleteWidget,
    isEditMode,
    isSaving,
    hasError,
    hasWidgets,
    pageExists
  } = useDynamicPageWidgets(pageType);
  
  // Modal state
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [pendingAddIndex, setPendingAddIndex] = useState<number | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | undefined>(undefined);
  
  // Loading states for add/delete operations
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [isSavingDelete, setIsSavingDelete] = useState(false);

  // Show widget selection modal
  const handleShowAddModal = useCallback((afterIndex?: number) => {
    setPendingAddIndex(afterIndex);
    setShowWidgetModal(true);
  }, []);

  // Handle widget selection from modal
  const handleSelectWidget = useCallback((widgetTypeOrTypes: string | string[]) => {
    const originalAfterIndex = pendingAddIndex !== undefined ? 
      widgets[pendingAddIndex]?.index : undefined;
    
    // Handle both single widget and multiple widgets
    if (Array.isArray(widgetTypeOrTypes)) {
      // Multiple widgets - add all to Redux first, then save once
      addMultipleWidgets(widgetTypeOrTypes as any[], originalAfterIndex);
    } else {
      // Single widget - existing behavior
      addWidget(widgetTypeOrTypes as any, null, originalAfterIndex);
    }
    
    // Save to API after all widgets are added to Redux
    dispatch(updateSiteContentRequest());
    setIsSavingAdd(true); // Track save state
    // Don't close modal or exit edit mode yet - wait for API success
  }, [addWidget, addMultipleWidgets, pendingAddIndex, widgets, dispatch]);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, []);

  // Show delete confirmation modal
  const handleShowDeleteModal = useCallback((widgetIndex: number) => {
    setPendingDeleteIndex(widgetIndex);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete - actually delete the section
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteIndex !== undefined) {
      deleteWidget(pendingDeleteIndex);
    }
    dispatch(updateSiteContentRequest()); // Save to API immediately
    setIsSavingDelete(true); // Track save state
    // Don't close modal or exit edit mode yet - wait for API success
  }, [deleteWidget, pendingDeleteIndex, dispatch]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, []);

  // ✅ Close add modal after API success
  useEffect(() => {
    if (isSavingAdd && !updateLoading) {
      setIsSavingAdd(false);
      setShowWidgetModal(false);
      setPendingAddIndex(undefined);
      dispatch(toggleEditMode());
    }
  }, [isSavingAdd, updateLoading, dispatch]);

  // ✅ Close delete modal after API success
  useEffect(() => {
    if (isSavingDelete && !updateLoading) {
      setIsSavingDelete(false);
      setShowDeleteModal(false);
      setPendingDeleteIndex(undefined);
      dispatch(toggleEditMode());
    }
  }, [isSavingDelete, updateLoading, dispatch]);

  // Show 404 Page Not Found if page doesn't exist (unless in edit mode)
  if (!pageExists && !isEditMode) {
    return <ErrorPage />;
  }

  // Show enhanced empty state when no widgets
  if (!hasWidgets) {
    return (
      <div className="min-h-screen flex flex-col" style={{ paddingTop: `${navSpacing + 32}px` }}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
              {isEditMode ? (
                // Enhanced empty state with icon in card
                <div className="space-y-6">
                  <div className="inline-block p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-full shadow-lg">
                    <MdWidgets className="w-20 h-20" style={{ color: '#EE4346' }} />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-3xl font-bold text-gray-900">No widgets yet</h3>
                    <p className="text-lg text-gray-600 max-w-md mx-auto">
                      Add your first widget to start building your page.
                    </p>
                  </div>
                  <div className="pt-4">
                    <button
                      onClick={() => handleShowAddModal()}
                      className="inline-flex items-center gap-3 px-10 py-5 text-white text-lg rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 group font-semibold"
                      style={{ backgroundColor: '#EE4346' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d63940'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EE4346'}
                    >
                      <MdAdd className="w-7 h-7 group-hover:rotate-90 transition-transform duration-300" />
                      <span>Add Your First Widget</span>
                    </button>
                  </div>
                </div>
              ) : (
                // Non-edit mode: elegant empty state
                <div className="space-y-6">
                  <div className="inline-block p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full shadow-md">
                    <MdOutlineInbox className="w-24 h-24 text-gray-300" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-semibold text-gray-800">We're working on this page</h3>
                    <p className="text-gray-500 max-w-sm mx-auto">
                      Please check back later for updates.
                    </p>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Edit Save Button */}
        {showEditButton && isEditControl && (
          <EditSaveButton
            isEditMode={isEditMode}
            onToggle={toggleEditModeWithSave}
            disabled={isSaving}
            variant="red"
            ariaLabel={isEditMode ? `Save ${pageType} Changes` : `Edit ${pageType}`}
          />
        )}

        {/* Widget Selection Modal */}
        <WidgetSelectionModal
          isOpen={showWidgetModal}
          onClose={handleCloseModal}
          onSelectWidget={handleSelectWidget}
        />
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error Loading {pageType}</h2>
          <p className="text-gray-600">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ paddingTop: `${navSpacing + 32}px` }}>
      {/* Custom children content (e.g., buttons, CTAs) */}
      {children && (
        <div className="sticky top-0 z-[100] bg-white shadow-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {children}
          </div>
        </div>
      )}
      {/* Dynamic Widget Rendering */}
      {widgets.map((widget, index) => {
        const { type, data } = widget;
        const Component = (widgetRegistry as any)[type];
        
        if (!Component) {
          console.warn(`Widget component not found for type: ${type}`);
          return (
            <div key={index} className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Not Found</h2>
                <p className="text-gray-600">Type: {type || 'Unknown'}</p>
                <p className="text-gray-600 mt-2">Available: {Object.keys(widgetRegistry).join(', ')}</p>
              </div>
            </div>
          );
        }

        return (
          <div key={`widget-container-${index}`} className="relative">
            <Suspense 
              key={`${type}-${index}`}
              fallback={<WidgetLoader />}
            >
              <Component 
                data={data}
                isEditing={isEditMode}
                isEditMode={isEditMode}
                onUpdate={(newData: any) => updateWidgetData(index, newData)}
                onCardClick={(item: any) => console.log(`${pageType} item clicked:`, item)}
              />
            </Suspense>

            {/* Add/Delete Section Controls - Always show in edit mode */}
            {isEditMode && (
              <div className="flex justify-center gap-6 py-4 bg-white border-t border-b border-gray-200">
                <button
                  onClick={() => handleShowAddModal(index)}
                  disabled={isSavingAdd}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group disabled:opacity-70 disabled:cursor-not-allowed"
                  title="Add section after this one"
                >
                  <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">{isSavingAdd ? "Adding..." : "Add Section"}</span>
                </button>

                <button
                  onClick={() => handleShowDeleteModal(index)}
                  disabled={isSavingDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl group disabled:opacity-70 disabled:cursor-not-allowed"
                  title="Delete this section"
                >
                  <FiTrash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">{isSavingDelete ? "Deleting..." : "Delete Section"}</span>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Edit Save Button */}
      {showEditButton && isEditControl && (
        <EditSaveButton
          isEditMode={isEditMode}
          onToggle={toggleEditModeWithSave}
          disabled={isSaving}
          variant="red"
          ariaLabel={isEditMode ? `Save ${pageType} Changes` : `Edit ${pageType}`}
        />
      )}

      {/* Widget Selection Modal */}
      <WidgetSelectionModal
        isOpen={showWidgetModal}
        onClose={handleCloseModal}
        onSelectWidget={handleSelectWidget}
        isLoading={isSavingAdd}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isSavingDelete}
      />
    </div>
  );
};

export default DynamicPage;
